import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { isTooManyEmailVerificationRequestsError } from '../../util/errors';
import { InlineTextButton, NamedLink } from '../../components';
import {
  DISMISS_STORAGE_KEY,
  shouldShowEmailVerificationBanner,
} from './emailVerificationBanner.helpers';

import css from './EmailVerificationBanner.module.css';

const readDismissedAt = () => {
  try {
    return Number(window.localStorage.getItem(DISMISS_STORAGE_KEY)) || null;
  } catch (e) {
    return null;
  }
};

/**
 * A slim, dismissible reminder to verify the email address. Replaces the modal that reopened on
 * every page; once closed it stays away for a few days.
 */
const EmailVerificationBanner = props => {
  const {
    className,
    currentUser,
    hasListingsOrOrders,
    currentPage,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = props;
  // localStorage only exists in the browser, so start hidden to match the server render
  const [dismissedAt, setDismissedAt] = useState(undefined);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    setDismissedAt(readDismissedAt());
  }, []);

  const show =
    dismissedAt !== undefined &&
    shouldShowEmailVerificationBanner({
      currentUser,
      hasListingsOrOrders,
      currentPage,
      dismissedAt,
    });
  if (!show) {
    return null;
  }

  const dismiss = () => {
    const now = Date.now();
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, String(now));
    } catch (e) {
      // Private mode etc.: it just hides for this page view
    }
    setDismissedAt(now);
  };

  const resend = () => {
    setResent(true);
    onResendVerificationEmail();
  };

  const status = sendVerificationEmailInProgress ? (
    <FormattedMessage id="EmailVerificationBanner.sending" />
  ) : sendVerificationEmailError ? (
    <FormattedMessage
      id={
        isTooManyEmailVerificationRequestsError(sendVerificationEmailError)
          ? 'EmailVerificationBanner.tooManyRequests'
          : 'EmailVerificationBanner.resendFailed'
      }
    />
  ) : resent ? (
    <FormattedMessage id="EmailVerificationBanner.sent" />
  ) : (
    <InlineTextButton rootClassName={css.link} onClick={resend}>
      <FormattedMessage id="EmailVerificationBanner.resend" />
    </InlineTextButton>
  );

  return (
    <div className={classNames(css.root, className)} role="status">
      <p className={css.text}>
        <FormattedMessage
          id="EmailVerificationBanner.text"
          values={{ email: <strong>{currentUser.attributes.email}</strong> }}
        />{' '}
        <span className={css.actions}>
          {status}
          <span aria-hidden="true"> · </span>
          <NamedLink className={css.link} name="ContactDetailsPage">
            <FormattedMessage id="EmailVerificationBanner.fixEmail" />
          </NamedLink>
        </span>
      </p>
      <button
        type="button"
        className={css.close}
        onClick={dismiss}
        aria-label="Dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
